import React from 'react';

import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';

import {
  PpmSuperactivityLineRow,
  PpmSuperactivityWorkbenchModel,
} from './procurement.models';

interface Props {
  open: boolean;
  loading: boolean;
  saving: boolean;

  model?: PpmSuperactivityWorkbenchModel | null;

  onClose: () => void;
  onSave: (ids: number[]) => void;
}

interface State {
  selectedIds: number[];
}

const money = (value: number) =>
  Number(value || 0).toLocaleString('fr-FR');

export default class PpmSuperactivityDialog extends React.Component<
  Props,
  State
> {
  state: State = {
    selectedIds: [],
  };

  componentDidUpdate(prevProps: Props) {
    if (
      this.props.model !== prevProps.model &&
      this.props.model
    ) {
      this.setState({
        selectedIds: this.props.model.lines
          .filter((x) => x.selected)
          .map((x) => Number(x.rid)),
      });
    }
  }

  toggle = (line: PpmSuperactivityLineRow) => {
    if (!this.props.model?.actions.canEdit) {
      return;
    }

    const id = Number(line.rid);

    this.setState((state) => {
      const selected = state.selectedIds.includes(id);

      return {
        selectedIds: selected
          ? state.selectedIds.filter((x) => x !== id)
          : [...state.selectedIds, id],
      };
    });
  };

  groupedAmount = () => {
    const { model } = this.props;

    if (!model) {
      return 0;
    }

    return model.lines
      .filter((x) =>
        this.state.selectedIds.includes(Number(x.rid))
      )
      .reduce(
        (total, x) => total + Number(x.avc || 0),
        0
      );
  };

  renderAssignment = (line: PpmSuperactivityLineRow) => {
    if (line.selected) {
      return (
        <Chip
          size="small"
          label="This Superactivity"
          color="primary"
          variant="outlined"
        />
      );
    }

    if (line.membership === 'OTHER') {
      return (
        <Chip
          size="small"
          label={
            line.othersuperactivitycode
              ? `${line.othersuperactivitycode} — ${line.othersuperactivityname || ''
              }`
              : 'Another Superactivity'
          }
          variant="outlined"
        />
      );
    }

    return (
      <Chip
        size="small"
        label="Available"
        variant="outlined"
      />
    );
  };

  render() {
    const {
      open,
      loading,
      saving,
      model,
      onClose,
      onSave,
    } = this.props;

    const canEdit = !!model?.actions.canEdit;

    return (
      <Dialog
        open={open}
        onClose={saving ? undefined : onClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {model
            ? `Manage Superactivity — ${model.superactivity.shc}`
            : 'Manage Superactivity'}
        </DialogTitle>

        <DialogContent dividers>
          {loading || !model ? (
            <Box
              sx={{
                py: 6,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6">
                  {model.superactivity.nam}
                </Typography>

                {model.superactivity.dsc && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    {model.superactivity.dsc}
                  </Typography>
                )}
              </Box>

              <Divider />

              <Stack spacing={0}>
                {model.lines.map((line) => {
                  const checked =
                    this.state.selectedIds.includes(
                      Number(line.rid)
                    );

                  return (
                    <Box
                      key={line.rid}
                      sx={{
                        py: 1.25,
                        borderBottom: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={2}
                      >
                        <FormControlLabel
                          sx={{
                            flex: 1,
                            m: 0,
                          }}
                          control={
                            <Checkbox
                              checked={checked}
                              disabled={!canEdit}
                              onChange={() =>
                                this.toggle(line)
                              }
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2">
                                {line.atx || line.nam}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {line.ccc} — {line.ccn}
                              </Typography>
                            </Box>
                          }
                        />

                        <Typography
                          variant="body2"
                          sx={{
                            minWidth: 130,
                            textAlign: 'right',
                          }}
                        >
                          CFA {money(line.avc)}
                        </Typography>

                        <Box sx={{ minWidth: 190 }}>
                          {this.renderAssignment(line)}
                        </Box>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>

              <Divider />

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle1">
                  Grouped Cost
                </Typography>

                <Typography variant="h6">
                  CFA {money(this.groupedAmount())}
                </Typography>
              </Stack>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Selecting an activity already assigned to another
                Superactivity will move it to this package when
                you save.
              </Typography>
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            disabled={
              loading ||
              saving ||
              !model ||
              !canEdit
            }
            onClick={() =>
              onSave(this.state.selectedIds)
            }
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}